/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import SideNav from '../../company-settings/side-nav/component';
import { AddTeamMember } from '../add-modal';
import { MEMBER_STATUS } from '../../../../constants/index';
import FilterableHeader from './filterable-header';
import { axiosInstanceB2B, Toaster } from '@share/utils';
import { useSelector } from 'react-redux';
import { ILoginState } from '@share/store/slices';
import { Urls } from '@share/constants';
import { SearchTable } from '../../common';
import { InputFilterType } from '@share/common-types';
import { get } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';

interface IMapStateToProps {
  loginStore: ILoginState;
}

const CompanyTeamCard: React.FC = () => {
  const login = useSelector((state: IMapStateToProps) => state.loginStore);
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setEditIsOpen] = useState(false);
  const [userData, setUserData] = useState([]);
  const [dptRoles, setDptRoles] = useState();
  const [selectedUser, setSelectedUser] = useState();
  const [filter, setFilter] = useState(MEMBER_STATUS.ALL);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({
    email: { value: null },
  });
  const [sorter, setSorter] = useState(null);

  const fetchUserData = async (page = 1, pageSize = 10, filters: any, sorter: any) => {
    setLoading(true);
    try {
      const sorterParam = !!sorter?.columnKey ? `orderBy=${sorter.columnKey}&sortOrder=${sorter.order === 'ascend' ? 'asc' : 'desc'}` : '';
      const filtersParam = !!filters?.email?.value ? `&email=${filters.email.value}` : '';
      const parentId = login.account?.name != 'travbusiness' ? login.account?.parentAccountId : login.account?.id;

      const { data: result } = await axiosInstanceB2B.get(`${Urls.GetTeamMembers}?orderBy=userId&sortOrder=desc&page=${page}&results=${pageSize}&accountId=${login.account?.id}${sorterParam}${filtersParam}`, {});
      const { data: getRoles } = await axiosInstanceB2B.get(`${Urls.GetUserRoles}?page=1&results=1000&accountId=${parentId}`, {});
      const updatedRoles = {
        ...getRoles,
        items: getRoles?.items?.map((item: any) =>
          item?.description === 'Traveler'
            ? { ...item, description: 'Employee/Traveler' }
            : item
        ),
      };
      setUserData(result.items);
      setTotalItems(result?.totalResults);
      setDptRoles(updatedRoles);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleSaveSuccess = async (values: any) => {
    setLoading(true);
    try {
      await axiosInstanceB2B.post(`/identity/users/company/save`, values);

      await fetchUserData(pagination?.current, pagination?.pageSize, filters, sorter);

      if (!values?.userId) {
        Toaster.success(`${intl.formatMessage({ id: 'team.member.added' })}`);
      } else {
        Toaster.success(`${intl.formatMessage({ id: 'team.member.updated' })}`);
      }
    } catch (error) {
      console.error(`${intl.formatMessage({ id: 'error.fetching.user.data' })}`, error);

      Toaster.error(`Error ${!values?.userId ? intl.formatMessage({ id: 'adding' }) : intl.formatMessage({ id: 'updating' })} ${intl.formatMessage({ id: 'error.fetching.user.data.message' })}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = userData?.filter((user: any) => {
    if (filter === MEMBER_STATUS?.ACTIVE) {
      return user.state === MEMBER_STATUS.ACTIVESTATE;
    } else if (filter === MEMBER_STATUS.INACTIVE) {
      return user.state !== MEMBER_STATUS?.ACTIVESTATE;
    }
    return true;
  });

  const handleEdit = (user: any) => {
    setEditIsOpen(true);
    setSelectedUser(user);
  };

  const handleOnTableChange = (pagination: any, filters: any, sorter: any) => {
    if (pagination) {
      setPagination(pagination as any);
    }

    if (!!filters.email?.length) {
      setFilters({
        email: { 
          value: get(get(filters.email, '[0]', '')?.split('~'), '[0]')
        }
      });
    }

    if (sorter) {
      setSorter(sorter as any);
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data: result } = await axiosInstanceB2B.get(`${Urls.GetDepartments}`);
      setDepartments(result?.items);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    fetchUserData(pagination?.current, pagination?.pageSize, filters, sorter);
  }, [pagination?.current, pagination?.pageSize, filters, sorter]);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const columns: any[] = [
    {
      description: intl.formatMessage({ id: 'team.member' }),
      key: 'fullName',
      type: InputFilterType.Text,
      width: 150,
      display: true,
      order: 10,
      sortable: false,
      component: (_text: any, record: any) => (
        <span>
          {record.firstName} {record.lastName}
        </span>
      )
    },
    {
      description: intl.formatMessage({ id: 'email' }),
      key: 'email',
      type: InputFilterType.Text,
      width: 180,
      display: true,
      filterFieldName: 'email',
      order: 20,
    },
    {
      description: intl.formatMessage({ id: 'status' }),
      key: 'state',
      type: InputFilterType.Text,
      width: 80,
      display: true,
      order: 40,
      sortable: false
    },
    {
      description: intl.formatMessage({ id: 'edit' }),
      key: 'edit',
      type: InputFilterType.Text,
      width: 40,
      display: true,
      order: 40,
      sortable: false,
      component: (_text: any, record: any) => (
        <a onClick={() => handleEdit(record)}>
          <i className="bi bi-pencil-square"></i>
        </a>
      ),
    },
  ];

  return (
    <>
      {isOpen && (
        <AddTeamMember
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          dptRoles={dptRoles}
          userData={{accountId: login.account?.id}}
          onSaveSuccess={handleSaveSuccess}
          departments={departments}
        />)}

      {isEditOpen && (
        <AddTeamMember
          isOpen={isEditOpen}
          setIsOpen={setEditIsOpen}
          dptRoles={dptRoles}
          userData={selectedUser}
          onSaveSuccess={handleSaveSuccess}
          departments={departments}
        />)}

      <section className="pt-3">
        <div className="container">
          <div className="row">
            <SideNav />
            <div className="col-lg-8 col-xl-9">
              <div className="card shadow-sm border">
                <div className="card-header bg-transparent border-bottom">
                  <h4 className="card-header-title">
                    <i className="bi bi-people mx-2"></i><FormattedMessage id="team.members" />
                  </h4>
                </div>
                <div className="card-body p-2 p-sm-4">
                  <FilterableHeader filter={filter} setFilter={setFilter} setIsOpen={setIsOpen} />

                  <div className="row mt-4">
                    <hr style={{ width: '-webkit-fill-available' }} />

                    {(loading || filteredData.length > 0 || !!filters.email.value) ? (
                      <Spin spinning={loading}>

                        <SearchTable
                          key={'active'}
                          title={''}
                          loading={loading}
                          allColumns={[...columns]}
                          dataSource={filteredData || []}
                          pagination={pagination}
                          filters={filters}
                          totalOrderCount={totalItems || 0}
                          rowKey={(row: any) => {
                            return row ? `${row?.userId}` : `${row?.userId}`;
                          } }
                  
                          hideRefresh={true} 
                          isFilterApplied={false}
                          onFiltersChange={() => {}}
                          onTableChange={handleOnTableChange}
                          onRefresh={() => {}}
                          onRowClick={() => {}}
                          onResetFilters={() => {}}
                          onApplyFilters={() => {}}
                          onExport={() => {}}
                        ></SearchTable>
                      </Spin>
                    ) : (
                      <div className="text-center"><FormattedMessage id="no.data" /></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CompanyTeamCard;
